/**
 * OpenAI (ChatGPT) Ads pixel
 *
 * WHY 不走 GTM：platform 的 GTM 容器權限不在我們手上（2026-10），改由程式直接注入 pixel loader
 * 並呼叫 measure。pixel ID 放環境設定，只有 PROD 有值；SIT / PREL 空字串＝不載入、不上報。
 * ⚠️ 與 GTM 的 OpenAI tag 只能擇一啟用，兩邊都活著同一筆轉換會被報兩次。
 *
 * loader 邏輯等同 OpenAI 提供的設定碼：先放一個佇列函式到 window.oaiq，script 載完前的呼叫會排隊，
 * 所以 init 之後立刻 measure 也不會漏。
 */

export const OPENAI_PIXEL_SDK_URL = 'https://bzrcdn.openai.com/sdk/oaiq.min.js';

type OaiqQueue = ((...args: unknown[]) => void) & { q?: unknown[] };

interface OaiqWindow extends Window {
  oaiq?: OaiqQueue;
}

/** OpenAI 事件名稱 → measure 參數。只列 OpenAI Ads 後台有定義的兩個轉換 */
const OPENAI_MEASURE_EVENTS: Record<string, { type: string }> = {
  registration_completed: { type: 'customer_action' },
  order_created: { type: 'contents' },
};

const getOaiq = (): OaiqQueue | undefined =>
  typeof window === 'undefined' ? undefined : (window as OaiqWindow).oaiq;

/**
 * 注入 pixel loader 並 init。回傳是否真的注入（已注入過或沒有 pixelId 都回 false）。
 * 可重複呼叫（App 每次 render 都會跑到），以 window.oaiq 是否存在去重，跟官方設定碼同一招。
 */
export const initOpenAIPixel = (pixelId?: string): boolean => {
  if (!pixelId || typeof window === 'undefined') return false;
  const w = window as OaiqWindow;
  if (w.oaiq) return false;

  const queue: OaiqQueue = function (this: unknown) {
    // 與官方設定碼一致：把 arguments 原樣排進佇列，SDK 載入後會逐筆 replay
    // eslint-disable-next-line prefer-rest-params
    queue.q?.push(arguments);
  };
  queue.q = [];
  w.oaiq = queue;

  const script = document.createElement('script');
  script.async = true;
  script.src = OPENAI_PIXEL_SDK_URL;
  const first = document.getElementsByTagName('script')[0];
  if (first?.parentNode) {
    first.parentNode.insertBefore(script, first);
  } else {
    document.head.appendChild(script);
  }

  // debug 一律關：官方給的設定碼帶 debug:true，正式環境不能開
  w.oaiq('init', { pixelId });
  return true;
};

/**
 * 把轉換事件轉送給 OpenAI pixel。不在對照表內的事件、或 pixel 未初始化（SIT/PREL）時 no-op。
 * 回傳是否有送出，方便測試與除錯。
 */
export const measureOpenAIConversion = (event: string): boolean => {
  const oaiq = getOaiq();
  const measure = OPENAI_MEASURE_EVENTS[event];
  if (!oaiq || !measure) return false;
  oaiq('measure', event, measure);
  return true;
};
